import React, { useState, useEffect, useCallback } from "react";
import { Button, Tab, Tabs, Badge, Modal, Form, Spinner } from "react-bootstrap";
import { FaPlus, FaCheck, FaTimes, FaFileUpload, FaDownload } from "react-icons/fa";

import InstitutionTable from "../components/institution/InstitutionTable";
import InstitutionForm from "../components/institution/InstitutionForm";

import {
    getInstitutions,
    createInstitution,
    updateInstitution,
    deleteInstitution,
    importInstitutions,
    downloadTemplate
} from "../api/services/InstitutionService";

const Institution = () => {
    const [institutions, setInstitutions] = useState([]);
    const [pendingInstitutions, setPendingInstitutions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("approved");

    // Form state
    const [showForm, setShowForm] = useState(false);
    const [selectedInstitution, setSelectedInstitution] = useState(null);
    const [formLoading, setFormLoading] = useState(false);

    // Import state
    const [showImport, setShowImport] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importMessage, setImportMessage] = useState(null);

    /* -------------------------------------------------------------------------- */
    /* FETCH INSTITUTIONS                                                         */
    /* -------------------------------------------------------------------------- */
    const fetchInstitutions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch approved
            const approvedData = await getInstitutions({ status: "approved" });
            setInstitutions(approvedData || []);

            // Fetch pending
            const pendingData = await getInstitutions({ status: "pending" });
            setPendingInstitutions(pendingData || []);

        } catch (err) {
            console.error("Error fetching institutions:", err);
            setError("Failed to load institutions");
            setInstitutions([]);
            setPendingInstitutions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    /* -------------------------------------------------------------------------- */
    /* CREATE / UPDATE HANDLER                                                     */
    /* -------------------------------------------------------------------------- */
    const handleSave = async (data) => {
        try {
            setFormLoading(true);

            if (selectedInstitution) {
                await updateInstitution(selectedInstitution._id, data);
            } else {
                // Admin created institutions are approved by default
                await createInstitution({ ...data, status: 'approved' });
            }

            setShowForm(false);
            setSelectedInstitution(null);
            fetchInstitutions();
        } catch (err) {
            console.error("Save failed:", err);
            setError(err.message || "Failed to save institution");
        } finally {
            setFormLoading(false);
        }
    };

    /* -------------------------------------------------------------------------- */
    /* DELETE HANDLER                                                             */
    /* -------------------------------------------------------------------------- */
    const handleDelete = async (institution) => {
        const confirmDelete = window.confirm(
            `Are you sure you want to delete ${institution.name}?`
        );

        if (!confirmDelete) return;

        try {
            await deleteInstitution(institution._id);
            fetchInstitutions();
        } catch (err) {
            console.error("Delete failed:", err);
            setError("Failed to delete institution");
        }
    };

    /* -------------------------------------------------------------------------- */
    /* IMPORT HANDLERS                                                            */
    /* -------------------------------------------------------------------------- */
    const handleImport = async () => {
        if (!importFile) return;

        try {
            setImportLoading(true);
            setImportMessage(null);

            const formData = new FormData();
            formData.append("file", importFile);

            const result = await importInstitutions(formData);

            setImportMessage({ type: 'success', text: result.message || "Import successful!" });
            setImportFile(null);

            // Refresh list
            fetchInstitutions();

            // Close after 2 seconds
            setTimeout(() => {
                setShowImport(false);
                setImportMessage(null);
            }, 2000);

        } catch (err) {
            console.error("Import failed:", err);
            setImportMessage({
                type: 'danger',
                text: err.response?.data?.message || "Failed to import file. Please check format."
            });
        } finally {
            setImportLoading(false);
        }
    };

    const handleDownloadTemplate = async () => {
        try {
            await downloadTemplate();
        } catch (err) {
            alert("Failed to download template");
        }
    };

    /* -------------------------------------------------------------------------- */
    /* APPROVE / REJECT HANDLER                                                   */
    /* -------------------------------------------------------------------------- */
    const handleApprove = async (institution) => {
        try {
            await updateInstitution(institution._id, { status: "approved" });
            fetchInstitutions();
        } catch (err) {
            console.error("Approve failed:", err);
            setError("Failed to approve institution");
        }
    };

    const handleReject = async (institution) => {
        if (!window.confirm(`Reject ${institution.name}?`)) return;
        try {
            await updateInstitution(institution._id, { status: "rejected" });
            fetchInstitutions();
        } catch (err) {
            console.error("Reject failed:", err);
            setError("Failed to reject institution");
        }
    };

    /* -------------------------------------------------------------------------- */
    /* EDIT HANDLER                                                               */
    /* -------------------------------------------------------------------------- */
    const handleEdit = (institution) => {
        setSelectedInstitution(institution);
        setShowForm(true);
    };

    useEffect(() => {
        fetchInstitutions();
    }, [fetchInstitutions]);

    /* -------------------------------------------------------------------------- */
    /* UI STATES                                                                  */
    /* -------------------------------------------------------------------------- */
    if (loading) {
        return (
            <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{ height: "50vh" }}
            >
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-3">Loading institutions...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="alert alert-danger mx-3 mt-3 d-flex justify-content-between align-items-center">
                <span>{error}</span>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={fetchInstitutions}
                >
                    Retry
                </Button>
            </div>
        );
    }

    /* -------------------------------------------------------------------------- */
    /* MAIN VIEW                                                                  */
    /* -------------------------------------------------------------------------- */
    return (
        <div>
            <header className="content-header d-flex justify-content-between align-items-center mb-4">
                <h1 className="mb-0">Institutions</h1>

                <div className="d-flex gap-2">
                    <Button
                        variant="outline-success"
                        className="d-flex align-items-center"
                        onClick={() => setShowImport(true)}
                    >
                        <FaFileUpload className="me-2" />
                        Bulk Import
                    </Button>

                    <Button
                        variant="primary"
                        className="d-flex align-items-center"
                        onClick={() => {
                            setSelectedInstitution(null);
                            setShowForm(true);
                        }}
                    >
                        <FaPlus className="me-2" />
                        Add Institution
                    </Button>
                </div>
            </header>

            <Tabs
                id="institution-tabs"
                activeKey={activeTab}
                onSelect={(k) => setActiveTab(k)}
                className="mb-3"
            >
                <Tab eventKey="approved" title="Approved Institutions">
                    <InstitutionTable
                        data={institutions}
                        pageSize={10}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                    />
                </Tab>

                <Tab
                    eventKey="pending"
                    title={
                        <span>
                            Pending Approvals
                            {pendingInstitutions.length > 0 &&
                                <Badge bg="danger" className="ms-2">{pendingInstitutions.length}</Badge>
                            }
                        </span>
                    }
                >
                    <InstitutionTable
                        data={pendingInstitutions}
                        pageSize={10}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        customActions={[
                            {
                                label: "Approve",
                                icon: <FaCheck />,
                                variant: "success",
                                onClick: handleApprove,
                                tooltip: "Approve Institution"
                            },
                            {
                                label: "Reject",
                                icon: <FaTimes />,
                                variant: "danger",
                                onClick: handleReject,
                                tooltip: "Reject Institution"
                            }
                        ]}
                    />
                </Tab>
            </Tabs>

            {/* ---------------------------------------------------------------------- */}
            {/* IMPORT MODAL                                                            */}
            {/* ---------------------------------------------------------------------- */}
            <Modal show={showImport} onHide={() => setShowImport(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Bulk Import Institutions</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <p className="text-muted small">
                            Upload an Excel/CSV file with the following columns: <br />
                            <code>Name, StateCode, DistrictName, Level, Type</code>
                        </p>
                        <Button
                            variant="link"
                            className="p-0 text-decoration-none mb-3"
                            onClick={handleDownloadTemplate}
                        >
                            <FaDownload className="me-1" /> Download Template
                        </Button>
                    </div>

                    <Form.Group controlId="formFile" className="mb-3">
                        <Form.Label>Select File</Form.Label>
                        <Form.Control
                            type="file"
                            accept=".xlsx, .xls, .csv"
                            onChange={(e) => setImportFile(e.target.files[0])}
                        />
                    </Form.Group>

                    {importMessage && (
                        <div className={`alert alert-${importMessage.type} mt-3`}>
                            {importMessage.text}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImport(false)}>
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleImport}
                        disabled={!importFile || importLoading}
                    >
                        {importLoading ? <Spinner size="sm" animation="border" /> : "Upload & Import"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ---------------------------------------------------------------------- */}
            {/* INSTITUTION FORM MODAL                                                  */}
            {/* ---------------------------------------------------------------------- */}
            <InstitutionForm
                show={showForm}
                onClose={() => {
                    setShowForm(false);
                    setSelectedInstitution(null);
                }}
                onSubmit={handleSave}
                initialData={selectedInstitution}
                loading={formLoading}
            />
        </div>
    );
};

export default Institution;
